import * as React from "react";
import {
  Search as SearchIcon,
  Loader2,
  Package,
  ShoppingCart,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useDebounce } from "use-debounce";
import { Dialog, DialogContent } from "@ui/core";
import { globalSearch, GlobalSearchResult } from "@shared/lib/api/search";
import { useTenant } from "@app/providers/tenant";
import { hasEffectivePermission, useRbac } from "@shared/rbac/guards";

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { user } = useRbac();
  const grants = ["catalog:read", "orders:read", "customers:read"].map(p => hasEffectivePermission(user, p));
  const grantsKey = grants.join(":");
  const canSearch = grants.some(Boolean);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const [results, setResults] = React.useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const resultsRef = React.useRef<HTMLDivElement>(null);

  // Keyboard binding
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (canSearch && e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [canSearch]);

  React.useEffect(() => {
    setResults(null); setFailed(false); setLoading(false);
  }, [query, tenantId, grantsKey]);

  React.useEffect(() => {
    let active = true;
    const value = debouncedQuery.trim();
    if (!open || !canSearch || !value || value !== query.trim()) return;
    setLoading(true); setFailed(false);
    globalSearch(value).then(result => {
      if (active) setResults(result);
    }).catch(() => {
      if (active) { setResults(null); setFailed(true); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debouncedQuery, query, open, tenantId, grantsKey, canSearch, retry]);

  const handleSelect = (path: string) => {
    navigate({ to: path });
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={!canSearch}
        aria-label={t("searchPlaceholder")}
        data-testid="global-search-trigger"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-background p-0 text-sm text-muted-foreground sm:w-full sm:max-w-sm sm:justify-start sm:gap-2 sm:px-3"
      >
        <SearchIcon size={14} />
        <span className="hidden sm:inline">{t("searchPlaceholder")}</span>
        <kbd className="pointer-events-none ms-auto hidden h-5 select-none items-center gap-1 rounded border bg-gray-100 px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 dark:bg-gray-800 md:inline-flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 sm:max-w-[550px] overflow-hidden gap-0">
          <div className="flex items-center border-b px-3">
            <SearchIcon className="me-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none focus:outline-none focus:ring-0 border-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t("topbar.searchPlaceholder")}
              aria-label={t("searchPlaceholder")}
              maxLength={200}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === "ArrowDown") {
                  event.preventDefault();
                  const first = resultsRef.current?.querySelector<HTMLButtonElement>("button[data-search-result]");
                  if (event.key === "Enter") first?.click(); else first?.focus();
                }
              }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div
            className="max-h-[300px] overflow-y-auto p-2"
            data-testid="search-results"
            ref={resultsRef}
            aria-busy={loading}
          >
            {loading && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                <Loader2 className="me-2 h-4 w-4 animate-spin inline" />{" "}
                {t("searching", "Searching...")}
              </div>
            )}

            {failed && <div role="alert" className="p-4 text-sm">
              <p>{t("searchFailed", "Search failed. Please try again.")}</p>
              <button type="button" className="mt-2 underline" onClick={() => setRetry(value => value + 1)}>{t("tryAgain", "Try again")}</button>
            </div>}
            {!loading && results && (
              <>
                {results.products.length === 0 &&
                  results.orders.length === 0 &&
                  results.customers.length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {t("noResults", "No results found.")}
                    </div>
                  )}

                {results.products.length > 0 && (
                  <div className="mb-2">
                    <div className="mb-1 px-2 text-xs font-semibold text-muted-foreground">
                      {t("nav:Products", {
                        ns: "nav",
                        defaultValue: "Products",
                      })}
                    </div>
                    {results.products.map((p) => (
                      <button
                        data-search-result
                        key={p.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(`/catalog/products/${p.id}/edit`);
                        }}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none hover:bg-gray-100 dark:hover:bg-gray-800 text-start focus:bg-gray-100 dark:focus:bg-gray-800"
                      >
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            className="h-6 w-6 rounded object-cover"
                          />
                        ) : (
                          <Package className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="flex-1 truncate">{p.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {t(p.status, p.status)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {results.orders.length > 0 && (
                  <div className="mb-2">
                    <div className="mb-1 px-2 text-xs font-semibold text-muted-foreground">
                      {t("nav:Orders", { ns: "nav", defaultValue: "Orders" })}
                    </div>
                    {results.orders.map((o) => (
                      <button
                        data-search-result
                        key={o.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(`/orders/${o.id}`);
                        }}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none hover:bg-gray-100 dark:hover:bg-gray-800 text-start focus:bg-gray-100 dark:focus:bg-gray-800"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        <span className="flex-1">
                          #{o.orderNumber} - {o.customerName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {o.status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {results.customers.length > 0 && (
                  <div className="mb-2">
                    <div className="mb-1 px-2 text-xs font-semibold text-muted-foreground">
                      {t("nav:Customers", {
                        ns: "nav",
                        defaultValue: "Customers",
                      })}
                    </div>
                    {results.customers.map((c) => (
                      <button
                        data-search-result
                        key={c.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(`/customers/${c.id}`);
                        }}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none hover:bg-gray-100 dark:hover:bg-gray-800 text-start focus:bg-gray-100 dark:focus:bg-gray-800"
                      >
                        <Users className="h-4 w-4" />
                        <span className="flex-1">{c.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {c.email}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            <div className="hidden sm:flex border-t px-2 py-1.5 text-xs text-muted-foreground justify-end gap-2 text-end">
              <span className="flex items-center justify-end gap-1">
                {t("escToClose", "Esc to close")}
              </span>
              <span className="flex items-center justify-end gap-1">
                {t("enterToSelect", "Enter to select")}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
